import React, { useEffect, useState, useRef, useMemo } from "react";
import { withRouter, Link } from "react-router-dom";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  UncontrolledTooltip,
  Input,
  Form,
  FormGroup,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";

import { Name, Email, Accounts } from "./contactlistCol";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";

import {
  getAdmins as onGetAdmins,
  addNewAdmin as onAddNewAdmin,
  updateAdmin as onUpdateAdmin,
  deleteAdmin as onDeleteAdmin,
} from "store/admins/actions";
import { isEmpty } from "lodash";

//redux
import { useSelector, useDispatch } from "react-redux";
import { getRoles } from "store/actions";

const AdminsList = props => {
  //meta title
  document.title = "Admin List | RKI";

  const dispatch = useDispatch();
  const [admin, setAdmin] = useState();
  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      name: (admin && admin.name) || "",
      email: (admin && admin.email) || "",
      role_id: admin?.roles?.length > 0 ? admin.roles[0].id : "",
      password: undefined,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Your Name"),
      email: Yup.string().required("Please Enter Your Email"),
      role_id: Yup.number().required(),
      password: Yup.string()
        .required()
        .min(8, "Password is too short - should be 8 chars minimum."),
      password_confirmation: Yup.string()
        .required()
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: values => {
      if (isEdit) {
        const updateAdmin = {
          id: admin.id,
          ...values,
        };

        // update admin
        dispatch(onUpdateAdmin(updateAdmin));
        validation.resetForm();
        setIsEdit(false);
      } else {
        const newAdmin = {
          ...values,
        };
        // save new admin
        dispatch(onAddNewAdmin(newAdmin));
        validation.resetForm();
      }
      toggle();
    },
  });

  const { admins, roles } = useSelector(state => ({
    admins: state.Admins.admins,
    roles: state.Roles.roles.filter(r => r.guard_name === "admin"),
  }));

  const [adminList, setAdminList] = useState([]);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filterable: true,
        Cell: cellProps => {
          return <Name {...cellProps} />;
        },
      },
      {
        Header: "Email",
        accessor: "email",
        filterable: true,
        Cell: cellProps => {
          return <Email {...cellProps} />;
        },
      },
      {
        Header: "Roles",
        accessor: "roles",
        filterable: true,
        Cell: cellProps => {
          return <Accounts {...cellProps} />;
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-success"
                onClick={() => {
                  const adminData = cellProps.row.original;
                  handleAdminClick(adminData);
                }}
              >
                <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
                <UncontrolledTooltip placement="top" target="edittooltip">
                  Edit
                </UncontrolledTooltip>
              </Link>
              <Link
                to="#"
                className="text-danger"
                onClick={() => {
                  const adminData = cellProps.row.original;
                  onClickDelete(adminData);
                }}
              >
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                <UncontrolledTooltip placement="top" target="deletetooltip">
                  Delete
                </UncontrolledTooltip>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    if (admins && !admins.length) {
      dispatch(onGetAdmins());
      setIsEdit(false);
    }
    if (roles && !roles.length) {
      dispatch(getRoles());
      setIsEdit(false);
    }
  }, [dispatch]);

  useEffect(() => {
    setAdmin(admins);
    setIsEdit(false);
  }, [admins]);

  useEffect(() => {
    if (!isEmpty(admins) && !!isEdit) {
      setAdmin(admins);
      setIsEdit(false);
    }
  }, [admins]);

  const toggle = () => {
    setModal(!modal);
  };

  const handleAdminClick = arg => {
    const admin = arg;

    setAdmin(admin);
    setIsEdit(true);

    toggle();
  };

  var node = useRef();
  const onPaginationPageChange = page => {
    if (
      node &&
      node.current &&
      node.current.props &&
      node.current.props.pagination &&
      node.current.props.pagination.options
    ) {
      node.current.props.pagination.options.onPageChange(page);
    }
  };

  //delete customer
  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = admins => {
    setAdmin(admins);
    setDeleteModal(true);
  };

  const handleDeleteAdmin = () => {
    dispatch(onDeleteAdmin(admin.id));
    onPaginationPageChange(1);
    setDeleteModal(false);
  };

  const handleAdminClicks = () => {
    setAdminList("");
    setIsEdit(false);
    toggle();
  };

  const keyField = "id";

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteAdmin}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Admins" breadcrumbItem="Admin List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={admins}
                    isGlobalFilter={true}
                    isAddOptions="Admin"
                    handleOptionClicks={handleAdminClicks}
                    customPageSize={10}
                    className="custom-header-css"
                  />

                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle} tag="h4">
                      {!!isEdit ? "Edit Admin" : "Add Admin"}
                    </ModalHeader>
                    <ModalBody>
                      <Form
                        onSubmit={e => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <Row>
                          <Col xs={12}>
                            <FormGroup>
                              <Label className="form-label">Name</Label>
                              <Input
                                name="name"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name || ""}
                                invalid={
                                  validation.touched.name &&
                                  validation.errors.name
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.touched.name &&
                                  validation.errors.name}
                              </FormFeedback>
                            </FormGroup>
                            <FormGroup>
                              <Label className="form-label">Email</Label>
                              <Input
                                name="email"
                                label="Email"
                                type="email"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.email || ""}
                                invalid={
                                  validation.touched.email &&
                                  validation.errors.email
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.touched.email &&
                                  validation.errors.email}
                              </FormFeedback>
                            </FormGroup>
                            <FormGroup>
                              <Label className="form-label">Role</Label>
                              <Input
                                type="select"
                                name="role_id"
                                className="form-select"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.role_id || ""}
                                invalid={
                                  validation.touched.role_id &&
                                  validation.errors.role_id
                                    ? true
                                    : false
                                }
                              >
                                <option></option>
                                {roles.map(role => (
                                  <option key={role.id} value={role.id}>
                                    {role.name}
                                  </option>
                                ))}
                              </Input>
                              <FormFeedback type="invalid">
                                {validation.touched.role_id &&
                                  validation.errors.role_id}
                              </FormFeedback>
                            </FormGroup>
                            <FormGroup>
                              <Label className="form-label">Password</Label>
                              <Input
                                name="password"
                                label="Password"
                                type="password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.password || ""}
                                invalid={
                                  validation.touched.password &&
                                  validation.errors.password
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.touched.password &&
                                  validation.errors.password}
                              </FormFeedback>
                            </FormGroup>
                            <FormGroup>
                              <Label className="form-label">
                                Confirm Password
                              </Label>
                              <Input
                                name="password_confirmation"
                                type="password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={
                                  validation.values.password_confirmation || ""
                                }
                                invalid={
                                  validation.touched.password_confirmation &&
                                  validation.errors.password_confirmation
                                    ? true
                                    : false
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.touched.password_confirmation &&
                                  validation.errors.password_confirmation}
                              </FormFeedback>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              <button
                                type="submit"
                                className="btn btn-success save-admin"
                              >
                                Save
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(AdminsList);
